import React, { useState } from 'react';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { api } from '../../lib/api';
import supportedLanguages from '../../translations/en/supportedLanguages.json';
import { Button } from '../../shared/FormStyles';
import Text from '../../shared/Text';
import FlagIcon from '../../shared/FlagIcon/index.jsx';

const RadioButton = ({ label, value, onChange, checked }) => {
  const code = value.substring(0, 3);

  return (
    <ButtonContainer active={checked}>
      <FlagIcon code={code} />
      <Text
        variant="small2"
        flex="1"
        alignSelf="center"
        textAlign="left"
        pl="10px"
        color={checked ? 'primary.100' : 'black.100'}
      >
        {label}
      </Text>
      <LanguageSelector
        name="language"
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
      />
    </ButtonContainer>
  );
};

const LanguageModal = () => {
  const currentUserLanguage = useSelector(state => state.userData.language);
  const [value, setValue] = useState(currentUserLanguage);
  const { languages, suggestedLanguages } = supportedLanguages;
  const { t } = useTranslation(['translationModal', 'shared']);
  const handleChange = e => {
    setValue(e.target.value);
  };

  const onClickSave = () => {
    api({
      method: 'patch',
      url: '/api/language_settings/update_language',
      data: { language: value },
    }).then(() => location.reload());
  };
  return (
    <>
      <Container>
        <Text
          variant="subtitle1"
          as="h3"
          mt="2px"
          mb="10px"
          textAlign="left"
          flexBasis="100%"
        >
          {t('translationModal:header')}
        </Text>
        <Text
          variant="subtitle2"
          as="h4"
          mt="5px"
          mb="10px"
          textAlign="left"
          flexBasis="100%"
        >
          {t('translationModal:suggestedLanguagesHeader')}
        </Text>
        {suggestedLanguages.map(language => (
          <RadioButton
            key={language.code}
            label={language.name}
            value={language.code}
            checked={language.code === value}
            onChange={handleChange}
          />
        ))}
        <Text
          variant="subtitle2"
          as="h4"
          mb="10px"
          textAlign="left"
          flexBasis="100%"
        >
          {t('allLanguagesHeader')}
        </Text>
        {languages.map(language => (
          <RadioButton
            key={language.code}
            label={language.name}
            value={language.code}
            checked={language.code === value}
            onChange={handleChange}
          />
        ))}
      </Container>
      <Text variant="small4" fontStyle="italic" as="p" my="12px">
        <Trans i18nKey="translationModal" t={t} components={{ a: <a /> }}>
          {t('translationModal:disclaimer')}
        </Trans>
      </Text>
      <ButtonGroup>
        <Button variant="containedBlue" onClick={onClickSave}>
          {t('shared:save')}
        </Button>
        <Button variant="tertiary" onClick={() => Swal.close()}>
          {t('shared:cancel')}
        </Button>
      </ButtonGroup>
    </>
  );
};

export default LanguageModal;

const Container = styled.div`
  overflow: auto;
  max-height: calc(100vh - 220px);
  ${themeGet('mediaQueries.forMediumUp')} {
    display: flex;
    flex-wrap: wrap;
  }
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 10px;
  &&& button {
    margin: 5px;
  }
`;

const ButtonContainer = styled.label`
  width: 100%;
  padding: 3px 15px;
  margin-bottom: 2px;
  display: flex;
  border-radius: 4px;
  background: ${props =>
    props.active ? themeGet('colors.primary.10') : 'transparent'};
  &:hover {
    background: ${themeGet('colors.primary.10')};
  }
  ${themeGet('mediaQueries.forMediumUp')} {
    width: 32%;
    &:hover {
      background: ${themeGet('colors.black.10')};
    }
  }
`;

const LanguageSelector = styled.input`
  &:not(:checked),
  &:checked {
    visibility: hidden;
    display: flex;
    align-items: center;
    align-self: center;
  }
  &:checked::after {
    content: '\f00c';
    font: var(--fa-font-regular);
    color: ${themeGet('colors.primary.100')};
    width: 21px;
    height: 12px;
    visibility: visible;
    display: block;
  }
`;
